import { useEffect, useState } from 'react';
import { customerService, type CustomerContact } from '@/services/customer.service';
import { Modal } from '@/components/shared/Modal';
import { FormField } from '@/components/shared/FormField';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from '@/lib/swal';

function apiMessage(error: unknown, fallback: string): string {
  const maybe = error as { response?: { data?: { error?: { message?: string } } } } | undefined;
  return maybe?.response?.data?.error?.message ?? fallback;
}

export function AddContactModal({
  open,
  onClose,
  customerId,
  locationId,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  customerId: string;
  locationId: string;
  onCreated: (contact: CustomerContact) => void;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [designation, setDesignation] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setFirstName('');
      setLastName('');
      setPhone('');
      setDesignation('');
    }
  }, [open]);

  const canSubmit = !!firstName.trim() && !!lastName.trim() && !!phone.trim() && !!locationId;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    try {
      const contact = await customerService.createContact(customerId, locationId, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: phone.trim(),
        designation: designation.trim() || undefined,
      });
      toast.success('Contact added');
      onCreated(contact);
      onClose();
    } catch (err) {
      toast.error(apiMessage(err, 'Could not add contact'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add Contact">
      <form onSubmit={submit} className="space-y-4">
        {!locationId && (
          <p className="rounded-md bg-amber-500/10 px-3 py-2 text-sm text-amber-600">
            Select or add a location first — a contact belongs to a location.
          </p>
        )}
        <FormField label="First Name" htmlFor="ct-first" required>
          <Input
            id="ct-first"
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            required
            disabled={saving || !locationId}
          />
        </FormField>
        <FormField label="Last Name" htmlFor="ct-last" required>
          <Input
            id="ct-last"
            value={lastName}
            onChange={(e) => setLastName(e.target.value)}
            required
            disabled={saving || !locationId}
          />
        </FormField>
        <FormField label="Phone" htmlFor="ct-phone" required>
          <Input
            id="ct-phone"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            required
            disabled={saving || !locationId}
          />
        </FormField>
        <FormField label="Designation" htmlFor="ct-desig">
          <Input
            id="ct-desig"
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
            disabled={saving || !locationId}
          />
        </FormField>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" disabled={saving || !canSubmit}>
            {saving ? 'Adding…' : 'Add Contact'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
